import {
  DoctorCreditSchema,
  DoctorPriceListSchema,
  DoctorStatementSchema,
  FinanceOverviewSchema,
  FinanceSettingsSchema,
  PaymentSummarySchema,
} from '@contracts/schemas/finance.schema';
import type {
  AdjustmentInput,
  CreditInput,
  DoctorCredit,
  DoctorPriceList,
  DoctorStatement,
  FinanceOverview,
  FinanceRepository,
  FinanceSettings,
  PaymentEdit,
  PaymentInput,
  PaymentSummary,
} from '@/application/ports/FinanceRepository';
import { request } from '../client';

/** The money of a case, over HTTP. The request of 8 October 2026 (fourth). */
export class HttpFinanceRepository implements FinanceRepository {
  summary(jobOrderNo: string): Promise<PaymentSummary> {
    return request(`/job-orders/${jobOrderNo}/payment-summary`, { schema: PaymentSummarySchema });
  }

  addAdjustment(jobOrderNo: string, input: AdjustmentInput): Promise<PaymentSummary> {
    return request(`/job-orders/${jobOrderNo}/adjustments`, {
      method: 'POST',
      body: { kind: input.kind, mode: input.mode, value: input.value, reason: input.reason },
      schema: PaymentSummarySchema,
    });
  }

  removeAdjustment(jobOrderNo: string, id: string): Promise<PaymentSummary> {
    return request(`/job-orders/${jobOrderNo}/adjustments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      schema: PaymentSummarySchema,
    });
  }

  addPayment(jobOrderNo: string, input: PaymentInput): Promise<PaymentSummary> {
    return request(`/job-orders/${jobOrderNo}/payments`, {
      method: 'POST',
      body: {
        amount: input.amount,
        method: input.method,
        reference: input.reference,
        note: input.note,
        ...(input.paidOn === undefined ? {} : { paidOn: input.paidOn }),
      },
      schema: PaymentSummarySchema,
    });
  }

  editPayment(jobOrderNo: string, id: string, edit: PaymentEdit): Promise<PaymentSummary> {
    return request(`/job-orders/${jobOrderNo}/payments/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: {
        amount: edit.amount,
        method: edit.method,
        reference: edit.reference,
        note: edit.note,
        ...(edit.paidOn === undefined ? {} : { paidOn: edit.paidOn }),
      },
      schema: PaymentSummarySchema,
    });
  }

  removePayment(jobOrderNo: string, id: string): Promise<PaymentSummary> {
    return request(`/job-orders/${jobOrderNo}/payments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      schema: PaymentSummarySchema,
    });
  }

  statement(doctorId: string): Promise<DoctorStatement> {
    return request(`/doctors/${encodeURIComponent(doctorId)}/statement`, { schema: DoctorStatementSchema });
  }

  addCredit(doctorId: string, input: CreditInput): Promise<DoctorCredit> {
    return request(`/doctors/${encodeURIComponent(doctorId)}/credit`, {
      method: 'POST',
      body: {
        amount: input.amount,
        note: input.note,
        method: input.method ?? null,
        reference: input.reference ?? null,
      },
      schema: DoctorCreditSchema,
    });
  }

  removeCredit(doctorId: string, input: { amount: number; note: string }): Promise<DoctorCredit> {
    return request(`/doctors/${encodeURIComponent(doctorId)}/credit/remove`, {
      method: 'POST',
      body: { amount: input.amount, note: input.note },
      schema: DoctorCreditSchema,
    });
  }

  overview(): Promise<FinanceOverview> {
    return request('/accounts/overview', { schema: FinanceOverviewSchema });
  }

  /* A doctor's own prices, and the currency. The lab's request of 17 October 2026, items 11 and 13. */
  doctorPrices(doctorId: string): Promise<DoctorPriceList> {
    return request(`/doctors/${encodeURIComponent(doctorId)}/prices`, { schema: DoctorPriceListSchema });
  }

  setDoctorPrice(doctorId: string, itemId: string, unitPrice: number): Promise<DoctorPriceList> {
    return request(`/doctors/${encodeURIComponent(doctorId)}/prices/${encodeURIComponent(itemId)}`, {
      method: 'PUT',
      body: { unitPrice },
      schema: DoctorPriceListSchema,
    });
  }

  clearDoctorPrice(doctorId: string, itemId: string): Promise<DoctorPriceList> {
    return request(`/doctors/${encodeURIComponent(doctorId)}/prices/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
      schema: DoctorPriceListSchema,
    });
  }

  settings(): Promise<FinanceSettings> {
    return request('/finance/settings', { schema: FinanceSettingsSchema });
  }

  setExchangeRate(usdRate: number): Promise<FinanceSettings> {
    return request('/finance/exchange-rate', {
      method: 'PUT',
      body: { usdRate },
      schema: FinanceSettingsSchema,
    });
  }
}
